/**
 * Snow Zone Data — Swedish postal code → snow zone mapping
 *
 * Based on Boverkets snözonskartor (EKS 11 / SS-EN 1991-1-3).
 * Swedish snow zones range from 1.0 (coastal south) to 4.5+ (mountain).
 *
 * Postal codes are mapped by their 2- or 3-digit prefix to keep the
 * dataset compact. If a more granular lookup is needed in the future,
 * this can be extended to full 5-digit ranges or replaced by an API call.
 *
 * Sources:
 * - Boverkets konstruktionsregler (EKS)
 * - SMHI snölastkarta
 * - Svenskt Träs spännviddstabeller
 */

// ─── Types ──────────────────────────────────────────────────

export interface SnowZoneEntry {
	/** 2-3 digit postal code prefix */
	prefix: string;
	/** Snow zone value (kN/m²) */
	zone: number;
	/** Region name for display */
	region: string;
}

// ─── Data ───────────────────────────────────────────────────

/**
 * Snow zone lookup table sorted by prefix.
 *
 * Zone values represent characteristic snow load on the ground (s_k)
 * in kN/m² per Swedish EKS standard.
 */
const SNOW_ZONE_TABLE: SnowZoneEntry[] = [
	// NOTE: prefixes follow the real Swedish postnummerområden. An earlier
	// version of this table had several bands parked one district off
	// (65 labelled Eskilstuna instead of Karlstad, 97 labelled Haparanda
	// instead of Luleå, …), which returned both the wrong region name AND
	// the neighbouring district's snow zone — under-dimensioning roof
	// joists for e.g. Karlstad. Keep prefix ↔ town pairs verified against
	// the postal directory when editing.

	// ── Skåne (21–29) — Zon 1.0–1.5 ──
	{ prefix: "21", zone: 1.0, region: "Malmö" },
	{ prefix: "22", zone: 1.0, region: "Lund" },
	{ prefix: "23", zone: 1.0, region: "Trelleborg/Svedala" },
	{ prefix: "24", zone: 1.0, region: "Eslöv/Höör" },
	{ prefix: "25", zone: 1.0, region: "Helsingborg" },
	{ prefix: "26", zone: 1.0, region: "Landskrona/Ängelholm" },
	{ prefix: "27", zone: 1.0, region: "Ystad/Simrishamn" },
	{ prefix: "28", zone: 1.5, region: "Hässleholm/Osby" },
	{ prefix: "29", zone: 1.0, region: "Kristianstad" },

	// ── Halland / Småland / Blekinge (30–39) — Zon 1.0–2.0 ──
	{ prefix: "30", zone: 1.5, region: "Halmstad" },
	{ prefix: "31", zone: 1.5, region: "Falkenberg/Varberg" },
	{ prefix: "33", zone: 1.5, region: "Värnamo/Gislaved" },
	{ prefix: "34", zone: 1.5, region: "Ljungby/Älmhult" },
	{ prefix: "35", zone: 1.5, region: "Växjö" },
	{ prefix: "36", zone: 1.5, region: "Tingsryd/Emmaboda" },
	{ prefix: "37", zone: 1.5, region: "Karlskrona/Karlshamn" },
	{ prefix: "38", zone: 1.5, region: "Nybro/Öland" },
	{ prefix: "39", zone: 1.5, region: "Kalmar" },

	// ── Göteborg / Bohuslän (40–45) — Zon 1.5 ──
	{ prefix: "40", zone: 1.5, region: "Göteborg" },
	{ prefix: "41", zone: 1.5, region: "Göteborg" },
	{ prefix: "42", zone: 1.5, region: "Hisingen/Kungälv" },
	{ prefix: "43", zone: 1.5, region: "Mölndal/Kungsbacka" },
	{ prefix: "44", zone: 1.5, region: "Kungälv/Stenungsund" },
	{ prefix: "45", zone: 1.5, region: "Uddevalla/Lysekil" },

	// ── Västergötland / Småland inland (46–57) — Zon 2.0 ──
	{ prefix: "46", zone: 2.0, region: "Trollhättan/Vänersborg" },
	{ prefix: "50", zone: 2.0, region: "Borås" },
	{ prefix: "51", zone: 2.0, region: "Kinna/Svenljunga" },
	{ prefix: "52", zone: 2.0, region: "Falköping/Tidaholm" },
	{ prefix: "53", zone: 2.0, region: "Lidköping/Skara" },
	{ prefix: "54", zone: 2.0, region: "Skövde/Mariestad" },
	{ prefix: "55", zone: 2.0, region: "Jönköping" },
	{ prefix: "56", zone: 2.0, region: "Huskvarna/Gränna" },
	{ prefix: "57", zone: 2.0, region: "Nässjö/Vetlanda" },

	// ── Östergötland / Sörmland / Gotland (58–64) — Zon 1.5–2.0 ──
	{ prefix: "58", zone: 2.0, region: "Linköping" },
	{ prefix: "59", zone: 2.0, region: "Motala/Mjölby" },
	{ prefix: "60", zone: 2.0, region: "Norrköping" },
	{ prefix: "61", zone: 2.0, region: "Nyköping/Oxelösund" },
	{ prefix: "62", zone: 1.5, region: "Visby/Gotland" },
	{ prefix: "63", zone: 2.0, region: "Eskilstuna" },
	{ prefix: "64", zone: 2.0, region: "Katrineholm/Strängnäs" },

	// ── Stockholm region (10–19) — Zon 2.0 ──
	{ prefix: "10", zone: 2.0, region: "Stockholm" },
	{ prefix: "11", zone: 2.0, region: "Stockholm" },
	{ prefix: "12", zone: 2.0, region: "Stockholm" },
	{ prefix: "13", zone: 2.0, region: "Stockholm/Nacka" },
	{ prefix: "14", zone: 2.0, region: "Huddinge/Tumba" },
	{ prefix: "15", zone: 2.0, region: "Södertälje" },
	{ prefix: "16", zone: 2.0, region: "Bromma/Vällingby" },
	{ prefix: "17", zone: 2.0, region: "Solna/Sundbyberg" },
	{ prefix: "18", zone: 2.0, region: "Täby/Danderyd" },
	{ prefix: "19", zone: 2.0, region: "Sigtuna/Märsta" },

	// ── Värmland / Närke (65–69) — Zon 2.5–3.0 ──
	{ prefix: "65", zone: 2.5, region: "Karlstad" },
	{ prefix: "66", zone: 2.5, region: "Säffle/Åmål" },
	{ prefix: "67", zone: 2.5, region: "Arvika/Eda" },
	{ prefix: "68", zone: 3.0, region: "Kristinehamn/Hagfors/Torsby" },
	{ prefix: "69", zone: 2.5, region: "Karlskoga/Hallsberg" },

	// ── Västerås / Uppsala / Örebro (70–75) — Zon 2.0–2.5 ──
	{ prefix: "70", zone: 2.0, region: "Örebro" },
	{ prefix: "71", zone: 2.0, region: "Örebro" },
	{ prefix: "72", zone: 2.5, region: "Västerås" },
	{ prefix: "73", zone: 2.5, region: "Köping/Arboga" },
	{ prefix: "74", zone: 2.0, region: "Enköping/Bålsta" },
	{ prefix: "75", zone: 2.0, region: "Uppsala" },

	// ── Dalarna / Gävleborg (76–82) — Zon 2.5–3.0 ──
	{ prefix: "76", zone: 2.5, region: "Norrtälje" },
	{ prefix: "77", zone: 2.5, region: "Ludvika/Smedjebacken" },
	{ prefix: "78", zone: 3.0, region: "Borlänge" },
	{ prefix: "79", zone: 3.0, region: "Falun" },
	{ prefix: "80", zone: 2.5, region: "Gävle" },
	{ prefix: "81", zone: 2.5, region: "Sandviken" },
	{ prefix: "82", zone: 3.0, region: "Söderhamn/Bollnäs" },

	// ── Fjäll-Dalarna (78x–79x specifika) — Zon 3.5 ──
	{ prefix: "786", zone: 3.5, region: "Idre/Särna" },
	{ prefix: "792", zone: 3.5, region: "Mora" },
	{ prefix: "793", zone: 3.5, region: "Leksand" },
	{ prefix: "794", zone: 3.5, region: "Orsa" },

	// ── Jämtland / Härjedalen (83–84) — Zon 3.0–3.5 ──
	{ prefix: "83", zone: 3.5, region: "Östersund/Jämtland" },
	{ prefix: "837", zone: 3.5, region: "Åre" },
	{ prefix: "84", zone: 3.0, region: "Ånge/Bräcke" },
	{ prefix: "842", zone: 3.5, region: "Sveg/Härjedalen" },

	// ── Västernorrland (85–89) — Zon 3.0 ──
	{ prefix: "85", zone: 3.0, region: "Sundsvall" },
	{ prefix: "86", zone: 3.0, region: "Timrå" },
	{ prefix: "87", zone: 3.0, region: "Härnösand" },
	{ prefix: "88", zone: 3.0, region: "Sollefteå" },
	{ prefix: "89", zone: 3.0, region: "Örnsköldsvik/Kramfors" },

	// ── Västerbotten (90–93) — Zon 3.0–3.5 ──
	{ prefix: "90", zone: 3.0, region: "Umeå" },
	{ prefix: "91", zone: 3.0, region: "Vännäs/Vindeln" },
	{ prefix: "92", zone: 3.5, region: "Lycksele/Storuman" },
	{ prefix: "93", zone: 3.0, region: "Skellefteå" },

	// ── Norrbotten (94–98) — Zon 3.0–4.5 ──
	{ prefix: "94", zone: 3.0, region: "Piteå/Älvsbyn" },
	{ prefix: "95", zone: 3.5, region: "Kalix/Haparanda/Tornedalen" },
	{ prefix: "96", zone: 3.5, region: "Boden/Jokkmokk" },
	{ prefix: "97", zone: 3.0, region: "Luleå" },
	{ prefix: "98", zone: 3.5, region: "Kiruna/Gällivare" },
	{ prefix: "980", zone: 4.5, region: "Karesuando/Kiruna landsbygd" },
	{ prefix: "981", zone: 3.5, region: "Kiruna" },
	{ prefix: "982", zone: 3.5, region: "Gällivare/Malmberget" },
	{ prefix: "983", zone: 4.5, region: "Pajala/Tornedalen" },
];

// Sort by prefix length descending so 3-digit prefixes match before 2-digit
const SORTED_TABLE = SNOW_ZONE_TABLE.toSorted(
	(a, b) =>
		b.prefix.length - a.prefix.length || a.prefix.localeCompare(b.prefix),
);

// ─── Public API ─────────────────────────────────────────────

/**
 * Look up the snow zone for a Swedish postal code.
 * Accepts full 5-digit codes or shorter prefixes.
 * Returns null if no match is found.
 */
export function lookupSnowZone(postalCode: string): SnowZoneEntry | null {
	const cleaned = postalCode.replace(/\s/g, "").replace(/^SE-?/i, "");

	if (!/^\d{2,5}$/.test(cleaned)) {
		return null;
	}

	for (const entry of SORTED_TABLE) {
		if (cleaned.startsWith(entry.prefix)) {
			return entry;
		}
	}

	return null;
}

/**
 * Get the default snow zone (used when no postal code is provided).
 * Defaults to zone 2.0 (Stockholm-area, middle of Sweden).
 */
export const DEFAULT_SNOW_ZONE = 2.0;
